import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { workshopDashboardService, PriorityJobItem } from '@/app/services/dashboardService';
import { JobCardPriority } from '@/app/types/jobCard';
import { AlertTriangle, Clock, Inbox } from 'lucide-react';

// Priority colors configuration - muted professional styling
const priorityConfig: Record<JobCardPriority, { label: string; className: string }> = {
  urgent: {
    label: 'Urgent',
    className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400',
  },
  high: {
    label: 'High',
    className: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300',
  },
  normal: {
    label: 'Normal',
    className: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400',
  },
  low: {
    label: 'Low',
    className: 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-500',
  },
};

interface PriorityItemProps {
  item: PriorityJobItem;
  onClick: () => void;
}

const PriorityItem: React.FC<PriorityItemProps> = ({ item, onClick }) => {
  const vehicleDisplay = [item.vehicle.make, item.vehicle.model].filter(Boolean).join(' ');
  const config = priorityConfig[item.priority];

  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors border-b last:border-b-0"
      onClick={onClick}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">
            {item.vehicle.registrationNumber}
          </span>
          {vehicleDisplay && (
            <span className="text-xs text-muted-foreground truncate">
              {vehicleDisplay}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {item.documentNumber}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        {/* Overdue badge */}
        {item.isOverdue && (
          <Badge
            variant="outline"
            className="text-xs font-normal bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400"
          >
            <Clock className="h-3 w-3 mr-1" />
            {item.daysOverdue}d overdue
          </Badge>
        )}
        {/* Priority badge */}
        <Badge variant="outline" className={cn('text-xs font-normal', config.className)}>
          {config.label}
        </Badge>
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
    <Inbox className="h-8 w-8 mb-2 opacity-50" />
    <p className="text-sm">No priority or overdue jobs</p>
  </div>
);

const SectionSkeleton: React.FC = () => (
  <div className="space-y-2">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center justify-between p-3">
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-20" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-14" />
        </div>
      </div>
    ))}
  </div>
);

const PriorityJobsPanel: React.FC = () => {
  const navigate = useNavigate();

  const { data: priorityJobs, isLoading } = useQuery({
    queryKey: ['workshopDashboard', 'priorityJobs'],
    queryFn: () => workshopDashboardService.getPriorityAndOverdueJobs(),
    refetchInterval: 60000, // Refresh every 60 seconds
    staleTime: 30000,
  });

  const handleItemClick = (id: number) => {
    navigate(`/workshop/job-cards/detail/${id}`);
  };

  const overdueCount = priorityJobs?.filter(j => j.isOverdue).length || 0;
  const urgentCount = priorityJobs?.filter(j => j.priority === 'urgent').length || 0;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-slate-500" />
            Priority & Overdue
          </CardTitle>
          <div className="flex gap-1.5">
            {overdueCount > 0 && (
              <Badge variant="outline" className="font-normal text-xs bg-red-50 text-red-700 border-red-200">
                {overdueCount} overdue
              </Badge>
            )}
            {urgentCount > 0 && (
              <Badge variant="outline" className="font-normal text-xs bg-slate-100 text-slate-700 border-slate-300">
                {urgentCount} urgent
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <SectionSkeleton />
        ) : !priorityJobs || priorityJobs.length === 0 ? (
          <EmptyState />
        ) : (
          <ScrollArea className="h-[280px]">
            <div className="border rounded-lg">
              {priorityJobs.map((item) => (
                <PriorityItem
                  key={item.id}
                  item={item}
                  onClick={() => handleItemClick(item.id)}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default PriorityJobsPanel;
